import { useMemo } from 'react';

interface RevenueDisplayProps {
  monthlyRevenue: number;
  nightlyRate: number;
  occupancyRate: number;
  annualRevenue: number;
  bedrooms: number;
  city: string;
  maxNightsPerYear: number | null;
  actualNightsPerYear: number;
  isCapped: boolean;
}

export function RevenueDisplay({
  monthlyRevenue,
  nightlyRate,
  occupancyRate,
  annualRevenue,
  bedrooms,
  city,
  maxNightsPerYear,
  actualNightsPerYear,
  isCapped,
}: RevenueDisplayProps) {
  const bookedNights = useMemo(
    () => Math.round(30 * (occupancyRate / 100)),
    [occupancyRate]
  );

  return (
    <div className="sticky top-28">
      {/* Main Revenue Card */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-xl overflow-hidden mb-5">
        <div className="bg-gradient-to-br from-[#882629] to-[#6E1F22] p-6 text-center">
          <p className="text-white/80 text-xs font-medium tracking-wider uppercase mb-2">
            Estimated Annual Revenue
          </p>
          <div className="flex items-center justify-center gap-1">
            <span className="text-white text-5xl lg:text-6xl font-bold font-['Fraunces']">
              ${annualRevenue.toLocaleString()}
            </span>
          </div>
          <p className="text-white/60 text-sm mt-2">per year</p>
          {isCapped && (
            <div className="mt-3 pt-3 border-t border-white/20">
              <p className="text-white/70 text-xs">
                Limited to {maxNightsPerYear} nights/year by local regulations
              </p>
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="text-center p-3 bg-[#F9F5F5] rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5">
                <i className="ri-money-dollar-circle-line text-[#882629] text-lg"></i>
              </div>
              <p className="text-lg font-bold text-[#505A5A]">
                ${nightlyRate}
              </p>
              <p className="text-[10px] text-[#505A5A]/60 font-medium">
                Nightly Rate
              </p>
            </div>
            <div className="text-center p-3 bg-[#F9F5F5] rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5">
                <i className="ri-calendar-check-line text-[#882629] text-lg"></i>
              </div>
              <p className="text-lg font-bold text-[#505A5A]">
                {occupancyRate}%
              </p>
              <p className="text-[10px] text-[#505A5A]/60 font-medium">
                Occupancy
              </p>
            </div>
            <div className="text-center p-3 bg-[#F9F5F5] rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center mx-auto mb-1.5">
                <i className="ri-moon-line text-[#882629] text-lg"></i>
              </div>
              <p className="text-lg font-bold text-[#505A5A]">
                {actualNightsPerYear}
              </p>
              <p className="text-[10px] text-[#505A5A]/60 font-medium">
                Nights/Year
              </p>
            </div>
          </div>

          {/* Monthly Average */}
          <div className="bg-gradient-to-r from-[#F9F5F5] to-[#FFF5F5] rounded-xl p-4 mb-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-[#505A5A]/60 font-medium">
                  Average Monthly Revenue
                </p>
                <p className="text-2xl font-bold text-[#882629] font-['Fraunces']">
                  ${Math.round(annualRevenue / 12).toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 flex items-center justify-center bg-[#882629]/10 rounded-full">
                <i className="ri-line-chart-line text-[#882629] text-xl"></i>
              </div>
            </div>
          </div>

          {/* Regulatory Notice */}
          {maxNightsPerYear !== null && (
            <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-start gap-2">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-alert-line text-amber-600 text-sm"></i>
                </div>
                <div>
                  <p className="text-xs font-semibold text-amber-900 mb-0.5">
                    Regulatory Cap Applied
                  </p>
                  <p className="text-[10px] text-amber-800 leading-relaxed">
                    {city} limits STR rentals to {maxNightsPerYear} nights per year. Revenue calculated based on this cap.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Revenue Breakdown Bar */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-[#505A5A] mb-2">
              Revenue Breakdown
            </p>
            <div className="space-y-2.5">
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#505A5A]/70">Gross Rental Income</span>
                  <span className="font-semibold text-[#505A5A]">
                    ${annualRevenue.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#882629] rounded-full"
                    style={{ width: '100%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#505A5A]/70">
                    After Management Fee (18%)
                  </span>
                  <span className="font-semibold text-[#505A5A]">
                    ${Math.round(annualRevenue * 0.82).toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#B8393D] rounded-full"
                    style={{ width: '82%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#505A5A]/70">After Expenses (~10%)</span>
                  <span className="font-semibold text-[#505A5A]">
                    ${Math.round(annualRevenue * 0.72).toLocaleString()}
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#D4686B] rounded-full"
                    style={{ width: '72%' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Market Context */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <p className="text-xs font-semibold text-[#505A5A] mb-3">
          Market Context — {city || 'Vancouver'}
        </p>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
              <i className="ri-arrow-up-line text-green-600 text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-medium text-[#505A5A]">
                High Demand Area
              </p>
              <p className="text-[10px] text-[#505A5A]/50">
                {bedrooms}BR properties are popular with travellers
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg flex-shrink-0">
              <i className="ri-sun-line text-amber-600 text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-medium text-[#505A5A]">
                Peak Season Boost
              </p>
              <p className="text-[10px] text-[#505A5A]/50">
                Jun–Sep can earn 25–40% more
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#F9F5F5] rounded-lg flex-shrink-0">
              <i className="ri-shield-check-line text-[#882629] text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-medium text-[#505A5A]">
                Compliance Required
              </p>
              <p className="text-[10px] text-[#505A5A]/50">
                STR licence needed in most cities
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
